// src/contexts/NotificationContext.js - VERSION CORRIGÉE
import React, { createContext, useContext, useState, useCallback } from 'react'
import { CToast, CToastBody, CToastHeader, CToaster } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { useTranslation } from 'react-i18next'
import { cilCheckCircle, cilWarning, cilBan, cilInfo } from '@coreui/icons'

const NotificationContext = createContext()

export const NotificationProvider = ({ children }) => {
  const { t } = useTranslation()
  const [toasts, setToasts] = useState([])

  // ✅ CORRECTION : Utilisation de useCallback pour stabiliser la référence
  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random()

    // Configuration des types de notification.
    // titleKey (clé i18n) au lieu d'un titre figé : traduit au RENDU (toasts
    // éphémères → réactif au changement de langue). Voir i18n S2 Lot B.
    const getNotificationConfig = (notificationType) => {
      switch (notificationType) {
        case 'success':
          return {
            color: 'success',
            backgroundColor: '#2eb85c',
            icon: cilCheckCircle,
            titleKey: 'common:toast_success',
          }
        case 'danger':
        case 'error':
          return {
            color: 'danger',
            backgroundColor: '#e55353',
            icon: cilBan,
            titleKey: 'common:toast_error',
          }
        case 'warning':
          return {
            color: 'warning',
            backgroundColor: '#ffc107',
            icon: cilWarning,
            titleKey: 'common:toast_warning',
            textColor: '#212529', // Texte foncé pour le warning
          }
        case 'info':
          return {
            color: 'info',
            backgroundColor: '#17a2b8',
            icon: cilInfo,
            titleKey: 'common:toast_info',
          }
        default:
          return {
            color: 'primary',
            backgroundColor: '#4f5d73',
            icon: cilInfo,
            titleKey: 'common:toast_info',
          }
      }
    }

    const config = getNotificationConfig(type)

    const toast = {
      id,
      message, // ← Le texte à afficher
      type, // ← Le type (success, danger, etc.)
      config, // ← Configuration visuelle
      duration,
      autohide: true,
    }

    console.log('🔔 Ajout toast:', { message, type, config: config.titleKey })

    setToasts((prevToasts) => [...prevToasts, toast])

    // Auto-suppression après la durée spécifiée
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((t) => t.id !== id))
    }, duration)
  }, []) // ← Tableau de dépendances vide = référence stable

  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id))
  }, [])

  return (
    <NotificationContext.Provider value={{ addToast, removeToast }}>
      {children}

      {/* Conteneur de toasts stylé */}
      <div
        className="toast-container"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 2000,
          minWidth: '350px',
          maxWidth: '450px',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => (
          <CToast
            key={toast.id}
            visible={true}
            className="mb-3 shadow-lg border-0"
            style={{
              pointerEvents: 'auto',
              backgroundColor: toast.config.backgroundColor,
              color: toast.config.textColor || '#ffffff',
              opacity: 1,
              transform: 'translateX(0)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '12px',
              overflow: 'hidden',
              minHeight: '70px',
              animation: 'slideInRight 0.3s ease-out',
            }}
            onClose={() => removeToast(toast.id)}
          >
            <CToastHeader
              closeButton
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'inherit',
                padding: '0.75rem 1rem',
              }}
            >
              <div className="d-flex align-items-center">
                <CIcon
                  icon={toast.config.icon}
                  className="me-2"
                  size="lg"
                  style={{ opacity: 0.9 }}
                />
                <strong className="me-auto">{t(toast.config.titleKey)}</strong>
              </div>
            </CToastHeader>
            <CToastBody style={{ padding: '1rem', fontSize: '0.95rem', lineHeight: '1.4' }}>
              {toast.message} {/* ← AFFICHE LE MESSAGE, PAS LE TYPE */}
            </CToastBody>
          </CToast>
        ))}
      </div>

      {/* Styles CSS intégrés */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .toast-container .toast {
          backdrop-filter: blur(10px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
        }

        .toast-container .toast .btn-close {
          filter: invert(1);
          opacity: 0.8;
        }

        .toast-container .toast .btn-close:hover {
          opacity: 1;
          transform: scale(1.1);
        }

        /* Animation de sortie */
        .toast-container .toast.hiding {
          transform: translateX(100%);
          opacity: 0;
          transition: all 0.3s ease-in;
        }
      `}</style>
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

export default NotificationContext
